import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { BookOpen, AlertCircle, Mail, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function LoginScreen() {
  const location = useLocation();
  const { sendMagicLink } = useAuth();

  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [sharedDevice, setSharedDevice] = useState(false);
  const [notice, setNotice] = useState(() => {
    try {
      return sessionStorage.getItem('lifestory:signedOutReason') === 'inactivity'
        ? 'You were signed out due to inactivity.'
        : null;
    } catch {
      return null;
    }
  });

  // Clear the one-time "signed out" reason so it doesn't persist on refresh.
  useEffect(() => {
    try { sessionStorage.removeItem('lifestory:signedOutReason'); } catch { /* ignore */ }
  }, []);

  // Where to land after sign-in completes (preserved through the email hop).
  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await sendMagicLink(email.trim(), { continuePath: from, sharedDevice });
      setSent(true);
    } catch (err) {
      console.error('Magic link error:', err);
      switch (err.code) {
        case 'auth/invalid-email':
        case 'functions/invalid-argument':
          setError('Please enter a valid email address');
          break;
        case 'auth/too-many-requests':
        case 'functions/resource-exhausted':
          setError('Too many attempts. Please try again in a few minutes.');
          break;
        default:
          setError('Could not send the sign-in link. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    setSent(false);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-100 to-white flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <a href="/" className="inline-flex items-center gap-2 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-quiltt-purple to-purple-600 rounded-xl flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">Life Story</span>
          </a>
          <h1 className="text-2xl font-bold text-quiltt-navy">Welcome</h1>
          <p className="text-gray-600 mt-1">Preserve memories that matter</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
          {sent ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Check your email</h2>
              <p className="text-gray-600 mb-1">
                We sent a sign-in link to
              </p>
              <p className="text-gray-900 font-medium mb-6 break-all">{email}</p>
              <p className="text-sm text-gray-500 mb-6">
                Open the email on this device and tap the link to sign in. The link
                expires after a short while for your security.
              </p>
              <button
                onClick={handleResend}
                className="text-quiltt-purple hover:underline text-sm font-medium"
              >
                Use a different email
              </button>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Sign in</h2>
              <p className="text-sm text-gray-500 mb-6">
                Enter your email and we'll send you a secure link to sign in — no
                password needed.
              </p>

              {notice && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                  {notice}
                </div>
              )}

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    autoFocus
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-quiltt-purple focus:border-transparent outline-none transition-shadow"
                  />
                </div>

                <label className="flex items-center gap-2 mb-4 text-sm text-gray-600 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={sharedDevice}
                    onChange={(e) => setSharedDevice(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-quiltt-purple focus:ring-quiltt-purple"
                  />
                  This is a shared or public device
                </label>

                <button
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="w-full flex items-center justify-center gap-2 bg-quiltt-purple hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending link...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      Send me a sign-in link
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
